import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../services/authServices';
import userService from '../services/userService';

const MyProfile = () => {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });

  useEffect(() => {
    const user = authService.getCurrentUser();
    setCurrentUser(user);
    setLoading(false);
  }, []);

  const handleChangePassword = async (e) => {
    e.preventDefault();

    // Validate passwords match
    if (passwordData.new_password !== passwordData.confirm_password) {
      alert('❌ New passwords do not match!');
      return;
    }

    // Validate password length
    if (passwordData.new_password.length < 6) {
      alert('❌ Password must be at least 6 characters!');
      return;
    }

    try {
      await userService.changeOwnPassword(
        passwordData.current_password,
        passwordData.new_password
      );
      alert('✅ Password changed successfully!');
      setShowPasswordModal(false);
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      alert('❌ ' + (error.response?.data?.message || 'Failed to change password'));
    }
  };

  if (loading) {
    return (
      <div style={styles.loading}>
        <div style={{ fontSize: '48px', marginBottom: '20px' }}>⏳</div>
        <h2>Loading profile...</h2>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>👤 My Profile</h1>
          <p style={styles.subtitle}>View and manage your account</p>
        </div>
        <button onClick={() => navigate('/dashboard')} style={styles.backButton}>
          ← Back to Dashboard
        </button>
      </div>

      {/* Profile Card */}
      <div style={styles.profileCard}>
        <div style={styles.profileHeader}>
          <div style={styles.avatar}>
            {currentUser?.full_name?.charAt(0).toUpperCase() || 'U'}
          </div>
          <div>
            <h2 style={styles.profileName}>{currentUser?.full_name}</h2>
            <p style={styles.profileRole}>
              <span style={getRoleStyle(currentUser?.role)}>
                {currentUser?.role === 'businessman' ? 'Businessman (Owner)' : 'Operator (Employee)'}
              </span>
            </p>
          </div>
        </div>

        <div style={styles.profileDetails}>
          <div style={styles.detailRow}>
            <div style={styles.detailLabel}>Username</div>
            <div style={styles.detailValue}>{currentUser?.username}</div>
          </div>
          <div style={styles.detailRow}>
            <div style={styles.detailLabel}>Email</div>
            <div style={styles.detailValue}>{currentUser?.email}</div>
          </div>
          <div style={styles.detailRow}>
            <div style={styles.detailLabel}>Role</div>
            <div style={styles.detailValue}>
              {currentUser?.role === 'businessman' ? 'Businessman (Owner)' : 'Operator (Employee)'}
            </div>
          </div>
          {currentUser?.businessAccess && currentUser.businessAccess.length > 0 && (
            <div style={styles.detailRow}>
              <div style={styles.detailLabel}>Business Access</div>
              <div style={styles.detailValue}>
                {currentUser.businessAccess.map(b => b.display_name).join(', ')}
              </div>
            </div>
          )}
        </div>

        <div style={styles.actionsContainer}>
          <button onClick={() => setShowPasswordModal(true)} style={styles.changePasswordButton}>
            🔑 Change Password
          </button>
        </div>
      </div>

      {/* Change Password Modal */}
      {showPasswordModal && (
        <div style={styles.modalOverlay} onClick={() => setShowPasswordModal(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>Change Password</h2>
            <form onSubmit={handleChangePassword}>
              <input
                type="password"
                placeholder="Current Password"
                value={passwordData.current_password}
                onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                style={styles.input}
                required
              />
              <input
                type="password"
                placeholder="New Password"
                value={passwordData.new_password}
                onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                style={styles.input}
                required
              />
              <input
                type="password"
                placeholder="Confirm New Password"
                value={passwordData.confirm_password}
                onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                style={styles.input}
                required
              />
              <div style={styles.modalButtons}>
                <button 
                  type="button" 
                  onClick={() => {
                    setShowPasswordModal(false);
                    setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
                  }} 
                  style={styles.cancelButton}
                >
                  Cancel
                </button>
                <button type="submit" style={styles.submitButton}>
                  Change Password
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Helper function
const getRoleStyle = (role) => {
  return role === 'businessman'
    ? { background: '#667eea', color: 'white', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' }
    : { background: '#48bb78', color: 'white', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' };
};

// Styles
const styles = {
  container: {
    minHeight: '100vh',
    background: '#f5f7fa',
    padding: '40px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '30px',
    flexWrap: 'wrap',
    gap: '20px',
  },
  title: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#2c3e50',
    margin: '0 0 4px 0',
  },
  subtitle: {
    color: '#7f8c8d',
    fontSize: '14px',
    margin: 0,
  },
  backButton: {
    padding: '10px 20px',
    background: '#95a5a6',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
  },
  profileCard: {
    background: 'white',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    maxWidth: '600px',
    margin: '0 auto',
  },
  profileHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
    padding: '30px',
    borderBottom: '2px solid #f0f0f0',
  },
  avatar: {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '36px',
    fontWeight: '700',
  },
  profileName: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#2c3e50',
    margin: '0 0 8px 0',
  },
  profileRole: {
    margin: 0,
  },
  profileDetails: {
    padding: '30px',
  },
  detailRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 0',
    borderBottom: '1px solid #f0f0f0',
  },
  detailLabel: {
    fontSize: '14px',
    color: '#7f8c8d',
    fontWeight: '600',
  },
  detailValue: {
    fontSize: '14px',
    color: '#2c3e50',
    fontWeight: '500',
  },
  actionsContainer: {
    padding: '30px',
    borderTop: '2px solid #f0f0f0',
    display: 'flex',
    justifyContent: 'center',
  },
  changePasswordButton: {
    padding: '12px 24px',
    background: '#3498db',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
  },
  loading: {
    padding: '60px 20px',
    textAlign: 'center',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    background: 'white',
    padding: '30px',
    borderRadius: '12px',
    width: '90%',
    maxWidth: '500px',
    boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
  },
  modalTitle: {
    fontSize: '22px',
    fontWeight: '700',
    marginBottom: '20px',
    color: '#2c3e50',
  },
  input: {
    width: '100%',
    padding: '12px',
    marginBottom: '16px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '14px',
    boxSizing: 'border-box',
  },
  modalButtons: {
    display: 'flex',
    gap: '12px',
    marginTop: '20px',
  },
  cancelButton: {
    flex: 1,
    padding: '12px',
    background: '#95a5a6',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
  },
  submitButton: {
    flex: 1,
    padding: '12px',
    background: '#3498db',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
  },
};

export default MyProfile;